import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api } from "../api";
import ProfessionalDetail from "./ProfessionalDetail";
import OrganizationDetail from "./OrganizationDetail";
import EventDetail from "./EventDetail";
import { PostCard } from "../components/Posts";

// Pagina pubblica di un singolo post condiviso (/p/post-{id}). Consultabile anche da anonimi.
function PostPage({ postId }) {
  const { t } = useTranslation();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/posts/${postId}`).then(({ data }) => setP(data)).catch(() => setErr(true)); }, [postId]);
  if (err) return <Layout><div className="yp-container py-8 text-red-600" data-testid="public-post-not-found">{t("sharex.not_found")}</div></Layout>;
  if (!p) return <Layout><div className="yp-container py-8" data-testid="public-post-loading">{t("common.loading")}</div></Layout>;
  return <Layout><div className="yp-container max-w-2xl py-8" data-testid="public-post-page"><PostCard p={p} canManage={false} onChanged={() => {}} /></div></Layout>;
}

// Risolve servizio/prodotto dal loro ID e reindirizza alla pagina pubblica reale (tab della struttura).
function EntityRedirect({ kind, entId }) {
  const { t } = useTranslation();
  const [to, setTo] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    const path = kind === "service" ? `/services/${entId}` : `/products/${entId}`;
    api.get(path).then(({ data }) => {
      const orgId = data.org_id;
      setTo(kind === "service" ? `/organizations/${orgId}?tab=services` : `/organizations/${orgId}?tab=shop&product=${entId}`);
    }).catch(() => setErr(true));
  }, [kind, entId]);
  if (err) return <Layout><div className="yp-container py-8 text-red-600" data-testid="public-entity-not-found">{t("sharex.not_found")}</div></Layout>;
  if (!to) return <Layout><div className="yp-container py-8" data-testid="public-entity-loading">{t("common.loading")}</div></Layout>;
  return <Navigate to={to} replace />;
}

// /p/{username}: il ruolo è deciso dal database. Namespace speciali gestiti dal backend per gli OG social:
// evento-{id}, servizio-{id}, prodotto-{id}, post-{id}.
export default function PublicProfile() {
  const { username } = useParams();
  const { t } = useTranslation();
  const slug = (username || "").toLowerCase();
  const eventId = slug.startsWith("evento-") ? username.slice("evento-".length) : null;
  const serviceId = slug.startsWith("servizio-") ? username.slice("servizio-".length) : null;
  const productId = slug.startsWith("prodotto-") ? username.slice("prodotto-".length) : null;
  const postId = slug.startsWith("post-") ? username.slice("post-".length) : null;
  const special = eventId || serviceId || productId || postId;
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (special) return;
    setRes(null); setErr(false);
    api.get(`/p/${encodeURIComponent(slug)}/resolve`).then(({ data }) => setRes(data)).catch(() => setErr(true));
  }, [username]);
  useEffect(() => {
    if (!res) return;
    let c = document.querySelector("link[rel='canonical']");
    if (!c) { c = document.createElement("link"); c.rel = "canonical"; document.head.appendChild(c); }
    c.href = `${window.location.origin}/p/${res.username}`;
  }, [res]);
  if (eventId) return <EventDetail idOverride={eventId} />;
  if (postId) return <PostPage postId={postId} />;
  if (serviceId) return <EntityRedirect kind="service" entId={serviceId} />;
  if (productId) return <EntityRedirect kind="product" entId={productId} />;
  if (err) return <Layout><div className="yp-container py-8 text-red-600" data-testid="public-profile-not-found">{t("sharex.not_found")}</div></Layout>;
  if (!res) return <Layout><div className="yp-container py-8" data-testid="public-profile-loading">{t("common.loading")}</div></Layout>;
  return res.role === "organization" ? <OrganizationDetail idOverride={res.id} /> : <ProfessionalDetail idOverride={res.id} />;
}
